import React, { useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import SlidingSideBar from '../SlidingSideBar/SlidingSideBar';
import CalculationWorksheet from './CalculationWorksheet';
import { Table as TableIcon } from 'react-bootstrap-icons';
import {
  SITE_IMPROVEMENT_ALLOWANCES_DOUBLE_TRIPLE_WIDE,
  SITE_IMPROVEMENT_ALLOWANCES_FOOTNOTE,
  SITE_IMPROVEMENT_ALLOWANCES_SINGLE_WIDE
} from '../../helpers/constants';

const ALLOWANCE_TYPES = [
  { id: 'single', label: 'Single Wide', data: SITE_IMPROVEMENT_ALLOWANCES_SINGLE_WIDE },
  { id: 'doubleTriple', label: 'Double/Triple Wide', data: SITE_IMPROVEMENT_ALLOWANCES_DOUBLE_TRIPLE_WIDE },
];

const AllowancesModal = ({ show, onHide }) => {
  const [selectedType, setSelectedType] = useState('single');
  const formatDollar = (n) => `$${Number(n).toLocaleString()}`;
  const currentData = ALLOWANCE_TYPES.find((t) => t.id === selectedType)?.data ?? SITE_IMPROVEMENT_ALLOWANCES_SINGLE_WIDE;
  const currentLabel = ALLOWANCE_TYPES.find((t) => t.id === selectedType)?.label ?? 'Single Wide';

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Site Improvement Allowances</Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-auto">
        <div className="mb-3 d-flex gap-2 align-items-center flex-wrap">
          <span className="fw-semibold">Type:</span>
          {ALLOWANCE_TYPES.map(({ id, label }) => (
            <Button
              key={id}
              variant={selectedType === id ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setSelectedType(id)}
            >
              {label}
            </Button>
          ))}
        </div>
        <h6 className="mb-2">{currentLabel}</h6>
        <Table bordered size="sm" className="mb-0">
          <thead className="">
            <tr>
              <th>Item</th>
              <th className="text-end">Standard Allowance</th>
              <th className="text-end">Maximum Allowance</th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((row, i) => {
              if (row.section) {
                return (
                  <tr key={i}>
                    <td colSpan={3} className="fw-semibold py-2">{row.section}</td>
                  </tr>
                );
              }
              return (
                <tr key={i} className={row.highlight ? 'table-light' : ''}>
                  <td style={row.indent ? { paddingLeft: '1.5rem' } : undefined}>{row.item}</td>
                  <td className="text-end">{formatDollar(row.standard)}</td>
                  <td className="text-end">{formatDollar(row.max)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <p className="text-muted small mt-3 mb-0 p-2 bg-light rounded">
          {SITE_IMPROVEMENT_ALLOWANCES_FOOTNOTE}
        </p>
      </Modal.Body>
    </Modal>
  );
};

const CalculationWorksheetSidebar = ({ visible, onClose, inventory, contact, contract, appChoices, onInventoryUpdate, onContactUpdate, onContractUpdate, inventoryUnitOptions, selectedInventoryId, onInventoryUnitChange
}) => {
  const [showAllowancesModal, setShowAllowancesModal] = useState(false);
  const showUnitPicker =
    Array.isArray(inventoryUnitOptions) &&
    inventoryUnitOptions.length > 1 &&
    typeof onInventoryUnitChange === 'function';

  return (
    <>
      <SlidingSideBar
        visible={visible}
        title="Calculation Worksheet"
        onClose={onClose}
        fullScreen
        showCloseButton
        additionalHeaderContent={
          <div className="d-flex align-items-center flex-wrap gap-2 mr-2">
            {showUnitPicker && (
              <div
                className="d-flex align-items-center gap-2 mb-0 flex-shrink-0"
                style={{ maxWidth: 'min(100%, 28rem)' }}
              >
                <Form.Label column={false} className="small font-weight-bold text-nowrap mb-0">
                  Inventory unit
                </Form.Label>
                <Form.Control
                  as="select"
                  size="sm"
                  className="small"
                  style={{ minWidth: '10rem', maxWidth: '20rem' }}
                  value={selectedInventoryId || inventory?._id || ''}
                  onChange={e => {
                    const unit = inventoryUnitOptions.find(u => u._id === e.target.value);
                    if (unit) onInventoryUnitChange(unit);
                  }}
                >
                  {inventoryUnitOptions.map(u => (
                    <option key={u._id} value={u._id}>
                      {u.serialA || u.invoice || u._id}
                    </option>
                  ))}
                </Form.Control>
              </div>
            )}
            <Button variant="primary" size="sm" onClick={() => setShowAllowancesModal(true)}>
              <TableIcon size={16} className="align-middle mr-1" />
              View Allowances
            </Button>
          </div>
        }
      >
        <div className="p-2 px-md-5 overflow-auto">
          {inventory && (
            <CalculationWorksheet
              inventory={inventory}
              contact={contact}
              contract={contract}
              appChoices={appChoices}
              onInventoryUpdate={onInventoryUpdate}
              onContactUpdate={onContactUpdate}
              onContractUpdate={onContractUpdate}
            />
          )}
        </div>
      </SlidingSideBar>
      <AllowancesModal show={showAllowancesModal} onHide={() => setShowAllowancesModal(false)} />
    </>
  );
};

export default CalculationWorksheetSidebar;
